<template>
  <div class="legal-page">
    <nav class="navbar">
      <router-link to="/" class="nav-logo">
        <AppLogo class="logo-img" />
      </router-link>
      <router-link to="/login" class="btn-login">Login</router-link>
    </nav>

    <div class="legal-container">
      <div class="legal-content">
        <h1>Privacy Policy</h1>
        <p class="last-updated">Last updated: January 29, 2026</p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            ResyMaid ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website at resymaid.com and our Chrome browser extension (collectively, the "Service").
          </p>
          <p>
            By using the Service, you agree to the collection and use of information in accordance with this policy.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>
          
          <h3>2.1 Information You Provide</h3>
          <ul>
            <li><strong>Account Information:</strong> Email address, name, and password when you create an account</li>
            <li><strong>Resume Data:</strong> The resume files you upload and the parsed content from those resumes (work experience, education, skills, contact information)</li>
            <li><strong>Job Information:</strong> Job descriptions and postings you choose to analyze</li>
            <li><strong>Payment Information:</strong> Processed securely by Stripe; we do not store your full credit card details</li>
          </ul>

          <h3>2.2 Information Collected Automatically</h3>
          <ul>
            <li><strong>Usage Data:</strong> Pages visited, features used, time spent on the Service</li>
            <li><strong>Device Information:</strong> Browser type, operating system, device identifiers</li>
            <li><strong>Analytics Data:</strong> We use Google Analytics to understand how users interact with our Service</li>
          </ul>

          <h3>2.3 Information from Third-Party Sign-In</h3>
          <p>
            If you sign in using Google, GitHub, or LinkedIn, we receive your name, email address, and profile picture from these services. We do not access your contacts, posts, or other account data.
          </p>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          <p>We use your information to:</p>
          <ul>
            <li>Provide and maintain the Service</li>
            <li>Generate AI-optimized resumes tailored to job descriptions</li>
            <li>Process payments and manage your account</li>
            <li>Send transactional emails (verification, password reset, receipts)</li>
            <li>Improve and personalize the Service</li>
            <li>Respond to your inquiries and support requests</li>
            <li>Detect and prevent fraud or abuse</li>
          </ul>
        </section>

        <section>
          <h2>4. Third-Party Services</h2>
          <p>We share your information with the following third-party services:</p>
          
          <table class="third-party-table">
            <thead>
              <tr>
                <th>Service</th>
                <th>Purpose</th>
                <th>Data Shared</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>OpenAI</td>
                <td>AI resume generation</td>
                <td>Resume content, job descriptions</td>
              </tr>
              <tr>
                <td>Stripe</td>
                <td>Payment processing</td>
                <td>Payment details, email, name</td>
              </tr>
              <tr>
                <td>Resend</td>
                <td>Transactional emails</td>
                <td>Email address, name</td>
              </tr>
              <tr>
                <td>MongoDB Atlas</td>
                <td>Data storage</td>
                <td>All account and resume data</td>
              </tr>
              <tr>
                <td>Google Analytics</td>
                <td>Usage analytics</td>
                <td>Anonymized usage data</td>
              </tr>
              <tr>
                <td>Vercel</td>
                <td>Hosting</td>
                <td>Server logs, IP addresses</td>
              </tr>
            </tbody>
          </table>

          <p>
            These services have their own privacy policies governing their use of your information. We encourage you to review their policies.
          </p>
        </section>

        <section>
          <h2>5. Data Storage and Security</h2>
          <p>
            Your data is stored on MongoDB Atlas servers located in the United States. We implement industry-standard security measures including:
          </p>
          <ul>
            <li>Encryption of data in transit (HTTPS/TLS)</li>
            <li>Encryption of data at rest</li>
            <li>Secure password hashing (bcrypt)</li>
            <li>Regular security audits</li>
          </ul>
          <p>
            While we strive to protect your information, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security.
          </p>
        </section>

        <section>
          <h2>6. Data Retention</h2>
          <p>
            We retain your personal information for as long as your account is active or as needed to provide you services. You may request deletion of your account and associated data at any time by contacting us.
          </p>
          <p>
            We may retain certain information as required by law or for legitimate business purposes (e.g., fraud prevention, resolving disputes).
          </p>
        </section>

        <section>
          <h2>7. Your Rights</h2>
          <p>Depending on your location, you may have the right to:</p>
          <ul>
            <li><strong>Access:</strong> Request a copy of your personal data</li>
            <li><strong>Correction:</strong> Update or correct inaccurate data</li>
            <li><strong>Deletion:</strong> Request deletion of your personal data</li>
            <li><strong>Portability:</strong> Receive your data in a portable format</li>
            <li><strong>Opt-out:</strong> Opt out of marketing communications</li>
          </ul>
          <p>
            To exercise these rights, contact us at <a href="mailto:support@resymaid.com">support@resymaid.com</a>.
          </p>
        </section>

        <section>
          <h2>8. Cookies and Tracking</h2>
          <p>
            We use cookies and similar technologies to:
          </p>
          <ul>
            <li>Keep you signed in</li>
            <li>Remember your preferences</li>
            <li>Analyze usage patterns (via Google Analytics)</li>
          </ul>
          <p>
            You can control cookies through your browser settings. Disabling cookies may affect the functionality of the Service.
          </p>
        </section>

        <section>
          <h2>9. Children's Privacy</h2>
          <p>
            The Service is not intended for users under 16 years of age. We do not knowingly collect personal information from children. If we learn we have collected data from a child, we will delete it promptly.
          </p>
        </section>

        <section>
          <h2>10. International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in the United States, where our servers and third-party service providers are located. By using the Service, you consent to this transfer.
          </p>
        </section>

        <section>
          <h2>11. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the new policy on this page and updating the "Last updated" date. Your continued use of the Service after changes constitutes acceptance of the updated policy.
          </p>
        </section>

        <section>
          <h2>12. Contact Us</h2>
          <p>
            If you have questions about this Privacy Policy or our data practices, contact us at:
          </p>
          <p class="contact-info">
            <strong>ResyMaid</strong><br>
            Email: <a href="mailto:support@resymaid.com">support@resymaid.com</a>
          </p>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import AppLogo from "@/components/AppLogo.vue";

export default {
  name: "PrivacyPolicy",
  components: { AppLogo },
};
</script>

<style scoped>
.legal-page {
  min-height: 100vh;
  background: #0a0a0f;
}

.navbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.nav-logo {
  text-decoration: none;
}

.logo-img {
  height: 56px;
}

.btn-login {
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 20px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.2s;
}

.btn-login:hover {
  background: rgba(255, 255, 255, 0.15);
}

.legal-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 3rem 2rem;
}

.legal-content {
  background: rgba(20, 20, 30, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 3rem;
}

h1 {
  color: #ffffff;
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.last-updated {
  color: #71717a;
  font-size: 14px;
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

section {
  margin-bottom: 2rem;
}

h2 {
  color: #ffffff;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 1rem;
  margin-top: 2rem;
}

h3 {
  color: #e4e4e7;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 0.75rem;
  margin-top: 1.5rem;
}

p {
  color: #a1a1aa;
  font-size: 15px;
  line-height: 1.7;
  margin-bottom: 1rem;
}

ul {
  color: #a1a1aa;
  font-size: 15px;
  line-height: 1.7;
  margin-left: 1.5rem;
  margin-bottom: 1rem;
}

li {
  margin-bottom: 0.5rem;
}

strong {
  color: #e4e4e7;
}

a {
  color: #8b5cf6;
  text-decoration: none;
}

a:hover {
  color: #a78bfa;
  text-decoration: underline;
}

.third-party-table {
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
  font-size: 14px;
}

.third-party-table th,
.third-party-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.third-party-table th {
  color: #e4e4e7;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.03);
}

.third-party-table td {
  color: #a1a1aa;
}

.contact-info {
  background: rgba(139, 92, 246, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.2);
  border-radius: 8px;
  padding: 1rem;
}

@media (max-width: 640px) {
  .legal-content {
    padding: 1.5rem;
  }
  
  h1 {
    font-size: 24px;
  }
  
  .third-party-table {
    font-size: 12px;
  }
  
  .third-party-table th,
  .third-party-table td {
    padding: 8px;
  }
}
</style>
